/**
 * FreeLessonRender.jsx — Lane 3A (Feb 2026) + Lane 4 opt-in save.
 * Renders the free-lesson taste from GET /free-lesson/by-token/:t
 * (anonymous rows + saved signed-in-free rows), OR from
 * sessionStorage (Lane 4 — signed-in FREE generations are no longer
 * auto-persisted; the client stashes them under
 * `free_lesson_session:${token}` and shows Save/Discard on this
 * page). Also renders a Delete button for already-saved rows owned
 * by the caller.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Printer,
  Sparkles,
  ChevronLeft,
  BookmarkPlus,
  Trash2,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import AtomLoader from "../components/AtomLoader";
import {
  fetchFreeLessonByToken,
  freeLessonPdfUrl,
  saveFreeLesson,
  deleteMyFreeLesson,
} from "../lib/freeLessonApi";
import { clearFreeLessonMarker } from "../lib/freeLessonMarker";
import { useAuth } from "../contexts/AuthContext";
import { isSignedInFreeUser } from "../lib/tier";

const FreeLessonRender = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFreeUser = isSignedInFreeUser(user);
  const [state, setState] = useState({
    loading: true,
    lesson: null,
    error: null,
    meta: null,
    // Lane 4 — origin of the payload:
    //   "server"        — row exists in db.free_lessons (already saved
    //                     OR anonymous). Show Delete only if the row
    //                     is the caller's own authenticated_free row.
    //   "sessionCache"  — freshly-generated free-tier lesson NOT yet
    //                     persisted. Show Save + Discard.
    origin: null,
    // True after a successful save from sessionCache mode.
    saved: false,
    saving: false,
    deleting: false,
    // True after Discard.
    discarded: false,
  });

  // Measure the fixed upgrade bar so lesson content always clears it (no
  // hardcoded guess — the bar wraps to two rows on mobile). We add the
  // measured height + iOS bottom safe-area inset as the content's bottom pad.
  const stickyBarRef = useRef(null);
  const [stickyBarHeight, setStickyBarHeight] = useState(160);
  useEffect(() => {
    const el = stickyBarRef.current;
    if (!el) return undefined;
    const measure = () => setStickyBarHeight(el.offsetHeight || 160);
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [state.loading, state.error]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFreeLessonByToken(token);
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          lesson: data?.lesson || null,
          meta: {
            grade: data?.grade || "",
            subject: data?.subject || "",
            transferable_skill: data?.transferable_skill || "",
            expectation_code: data?.expectation_code || "",
            expectation_text: data?.expectation_text || "",
            include_worksheet: data?.include_worksheet !== false,
            include_exit_ticket: data?.include_exit_ticket !== false,
            origin_field: data?.origin || null,
          },
          error: null,
          origin: "server",
        }));
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        // Lane 4 — 404 might mean this is a freshly-generated lesson
        // that was never saved (the /authenticated endpoint no longer
        // persists). Try the sessionStorage fallback before erroring.
        if (status === 404) {
          try {
            const cached = window.sessionStorage.getItem(
              `free_lesson_session:${token}`,
            );
            if (cached) {
              const parsed = JSON.parse(cached);
              setState((prev) => ({
                ...prev,
                loading: false,
                lesson: parsed.lesson || null,
                meta: {
                  grade: parsed.grade || "",
                  subject: parsed.subject || "",
                  transferable_skill: parsed.transferable_skill || "",
                  expectation_code: parsed.expectation_code || "",
                  expectation_text: parsed.expectation_text || "",
                  include_worksheet: parsed.include_worksheet !== false,
                  include_exit_ticket: parsed.include_exit_ticket !== false,
                  origin_field: null,
                },
                origin: "sessionCache",
                error: null,
              }));
              return;
            }
          } catch (_e) {
            /* fall through to the standard error UI */
          }
          clearFreeLessonMarker();
        }
        setState((prev) => ({
          ...prev,
          loading: false,
          lesson: null,
          error:
            err?.response?.data?.detail ||
            (status === 404
              ? "This lesson has expired. Start a fresh free lesson from the homepage."
              : "We couldn't load this lesson. Please try again."),
          meta: null,
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ---------- Lane 4 actions: save / discard / delete ---------------
  const handleSave = async () => {
    if (state.saving || state.origin !== "sessionCache") return;
    setState((s) => ({ ...s, saving: true }));
    try {
      const res = await saveFreeLesson({
        token,
        grade: state.meta.grade,
        subject: state.meta.subject,
        transferable_skill: state.meta.transferable_skill,
        context: "",
        expectation_code: state.meta.expectation_code || null,
        expectation_text: state.meta.expectation_text || null,
        include_worksheet: state.meta.include_worksheet,
        include_exit_ticket: state.meta.include_exit_ticket,
        lesson_json: state.lesson,
      });
      setState((s) => ({
        ...s,
        saving: false,
        saved: true,
        origin: "server",
        meta: { ...(s.meta || {}), origin_field: "authenticated_free" },
      }));
      toast.success(
        `Saved. ${res.saved_remaining ?? 0} of ${res.saved_limit ?? 15} free saves left.`,
      );
      // Clear the sessionStorage copy — server is now authoritative.
      try {
        window.sessionStorage.removeItem(`free_lesson_session:${token}`);
      } catch (_e) {
        /* ignore */
      }
    } catch (err) {
      setState((s) => ({ ...s, saving: false }));
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.code === "free_saved_cap_reached") {
        toast.error(
          `Save library is full (${detail.saved_used}/${detail.saved_limit}). Delete a saved lesson or start a 7-day trial.`,
        );
      } else {
        toast.error(
          typeof detail === "string" ? detail : "We couldn't save your lesson.",
        );
      }
    }
  };

  const handleDiscard = () => {
    try {
      window.sessionStorage.removeItem(`free_lesson_session:${token}`);
    } catch (_e) {
      /* ignore */
    }
    setState((s) => ({ ...s, discarded: true }));
    toast.success("Discarded. Generate another whenever you're ready.");
    navigate("/my-free-lessons");
  };

  const handleDelete = async () => {
    if (state.deleting) return;
    if (!window.confirm("Delete this saved free lesson? This can't be undone.")) return;
    setState((s) => ({ ...s, deleting: true }));
    try {
      await deleteMyFreeLesson(token);
      toast.success("Deleted. That save-slot is free again.");
      navigate("/my-free-lessons");
    } catch (err) {
      setState((s) => ({ ...s, deleting: false }));
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "We couldn't delete this lesson.",
      );
    }
  };

  const canSave = state.origin === "sessionCache" && !state.saved && isFreeUser;
  const canDelete =
    state.origin === "server" &&
    state.meta?.origin_field === "authenticated_free" &&
    isFreeUser;

  // (guarded early-return states rendered below)

  if (state.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="free-lesson-render-loading">
        <AtomLoader message="Loading your free lesson…" />
      </div>
    );
  }

  if (state.error || !state.lesson) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="free-lesson-render-error">
        <div className="max-w-md text-center">
          <p className="text-[#1c2d3d] font-bold text-lg">{state.error}</p>
          <Link
            to="/"
            data-testid="free-lesson-render-back-home"
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1e2c] transition"
          >
            <ChevronLeft className="h-4 w-4" /> Back to homepage
          </Link>
        </div>
      </div>
    );
  }

  const { lesson, meta } = state;
  const pdfHref = freeLessonPdfUrl(token);

  return (
    <div
      className="bg-[#f4f5f7] text-[#4a6984] min-h-screen"
      style={{
        paddingBottom: `calc(${stickyBarHeight}px + env(safe-area-inset-bottom, 0px) + 1.5rem)`,
      }}
    >
      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="free-lesson-render-page">
        {/* Eyebrow */}
        <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#96ded1]">
          Atomic Edu · Free lesson preview
        </p>

        {/* Title */}
        <h1
          data-testid="free-lesson-title"
          className="mt-3 font-black text-3xl sm:text-4xl lg:text-5xl text-[#1c2d3d] tracking-tight leading-[1.05]"
        >
          {lesson.activity_name || "Your free lesson"}
        </h1>

        {/* Meta row */}
        <p className="mt-3 text-sm text-[#4a6984]/85 font-semibold">
          {[meta.grade, meta.subject, meta.transferable_skill].filter(Boolean).join(" · ")}
        </p>
        {/* Ontario Curriculum Link — rendered from the teacher's stored
            selection only, never from model output. Omitted when none. */}
        {(meta.expectation_code || meta.expectation_text) && (
          <p
            data-testid="free-lesson-expectation"
            className="mt-2 text-sm italic text-[#4a6984]/70"
          >
            {[meta.expectation_code, meta.expectation_text].filter(Boolean).join(" — ")}
          </p>
        )}

        {/* Lane 4 — Save / Discard / Delete actions.
            - `canSave` fires on a sessionCache origin: freshly-generated
               lesson not yet persisted. Two buttons, "Save to my free
               lessons" (POST /free-lesson/save) and "Discard" (clear
               session + go home).
            - `canDelete` fires on a server-origin row that belongs to
               the caller (`origin_field == "authenticated_free"`) —
               DELETE /free-lesson/mine/:token.
            - Anonymous visitors and paid users see neither button. */}
        {canSave || canDelete ? (
          <div
            className="mt-6 flex flex-wrap gap-3"
            data-testid="free-lesson-actions-row"
          >
            {canSave ? (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={state.saving}
                  data-testid="free-lesson-save-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1e2c] transition disabled:opacity-60"
                >
                  <BookmarkPlus className="h-4 w-4" />
                  {state.saving ? "Saving…" : "Save to my free lessons"}
                </button>
                <button
                  type="button"
                  onClick={handleDiscard}
                  disabled={state.saving}
                  data-testid="free-lesson-discard-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#1c2d3d] font-extrabold text-sm border border-[#4a6984]/25 hover:bg-[#f4f5f7] transition disabled:opacity-60"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Discard
                </button>
                <p className="w-full text-[11px] font-semibold text-[#4a6984]/70 mt-1">
                  Free saves count against your 15-lesson mini-library. Discarding
                  keeps the monthly generation but nothing is saved.
                </p>
              </>
            ) : null}
            {state.saved ? (
              <span
                data-testid="free-lesson-saved-flag"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#96ded1]/25 border border-[#96ded1]/40 text-[#1c2d3d] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em]"
              >
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={state.deleting}
                data-testid="free-lesson-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-[#7a1c1c] font-extrabold text-xs uppercase tracking-[0.14em] border border-[#7a1c1c]/25 hover:bg-[#fdf1f1] transition disabled:opacity-60"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {state.deleting ? "Deleting…" : "Delete saved lesson"}
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Print action */}
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="free-lesson-print-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#96ded1] text-[#1c2d3d] font-extrabold text-sm border border-[#96ded1] hover:bg-[#7ec9be] transition"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </a>
          <Link
            to="/"
            data-testid="free-lesson-back-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#4a6984] font-bold text-sm border border-[#4a6984]/25 hover:bg-[#96ded1]/15 transition"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </Link>
        </div>

        <div className="mt-10 space-y-8">
          <Section kicker="Learning goal" heading="We are learning to">
            <p>{lesson.walt}</p>
          </Section>

          {Array.isArray(lesson.success_criteria) && lesson.success_criteria.length > 0 && (
            <Section kicker="Success criteria" heading="I can…">
              <BulletList items={lesson.success_criteria} />
            </Section>
          )}

          {lesson.lesson_overview && (
            <Section kicker="Lesson overview" heading="How the lesson runs">
              <p className="whitespace-pre-line">{lesson.lesson_overview}</p>
            </Section>
          )}

          {Array.isArray(lesson.teacher_moves) && lesson.teacher_moves.length > 0 && (
            <Section kicker="Teacher moves" heading="What you do in the room">
              <ul className="space-y-4" data-testid="free-lesson-teacher-moves">
                {lesson.teacher_moves.map((move, i) => {
                  if (!move) return null;
                  const doText = typeof move === "string" ? move : move?.do || "";
                  const listenText = typeof move === "string" ? "" : move?.listen_for || "";
                  return (
                    <li key={i} className="flex items-start">
                      <span className="ae-marker mt-2.5" />
                      <div>
                        {doText && (
                          <p>
                            <span className="font-extrabold text-[#1c2d3d]">You do: </span>
                            <span>{doText}</span>
                          </p>
                        )}
                        {listenText && (
                          <p className="mt-1">
                            <span className="font-bold text-[#4a6984]/85">Listen for: </span>
                            <span className="italic">{listenText}</span>
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {lesson.career_connection && (
            <Section kicker="Career connection" heading="Why this matters beyond the classroom">
              <p className="whitespace-pre-line">{lesson.career_connection}</p>
            </Section>
          )}

          {meta.include_worksheet && lesson.worksheet && (
            <Section kicker="Student worksheet" heading={lesson.worksheet.title || "Worksheet"}>
              {(lesson.worksheet.instructions || lesson.worksheet.intro) && (
                <p className="mb-4 whitespace-pre-line">{lesson.worksheet.instructions || lesson.worksheet.intro}</p>
              )}
              <BulletList
                items={lesson.worksheet.items || lesson.worksheet.tasks || []}
              />
            </Section>
          )}

          {meta.include_exit_ticket && lesson.exit_ticket && (
            <Section
              kicker="Exit ticket"
              heading={lesson.exit_ticket.title || "Exit ticket"}
            >
              {lesson.exit_ticket.prompt && (
                <p className="mb-4 whitespace-pre-line">{lesson.exit_ticket.prompt}</p>
              )}
              <BulletList items={lesson.exit_ticket.questions || []} />
            </Section>
          )}
        </div>
      </main>

      {/* Sticky trial CTA — currently-true copy only. */}
      <div
        ref={stickyBarRef}
        data-testid="free-lesson-sticky-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-[#1c2d3d] text-white shadow-2xl"
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Sparkles className="h-5 w-5 mt-0.5 text-[#96ded1] shrink-0" />
            <div className="min-w-0">
              <p className="font-extrabold text-sm sm:text-base leading-snug">
                Want the full version?
              </p>
              <p className="text-white/80 text-xs sm:text-sm leading-snug">
                Slide decks, differentiation, unlimited planning.
              </p>
            </div>
          </div>
          <Link
            to="/pricing"
            data-testid="free-lesson-sticky-cta-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#96ded1] text-[#1c2d3d] font-extrabold text-sm hover:bg-[#7ec9be] transition shrink-0 whitespace-nowrap"
          >
            Start your 7-day free trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const Section = ({ kicker, heading, children }) => (
  <section className="ae-card p-6 md:p-7" data-testid={`free-lesson-section-${(kicker || "").toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#96ded1]">
      {kicker}
    </p>
    <h2 className="mt-2 font-extrabold text-xl text-[#1c2d3d] tracking-tight">
      {heading}
    </h2>
    <div className="mt-4 text-[#4a6984] font-medium text-base leading-relaxed">
      {children}
    </div>
  </section>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2">
    {(items || []).map((item, i) => {
      if (!item) return null;
      const text = typeof item === "string" ? item : item?.text || String(item);
      return (
        <li key={i} className="flex items-start">
          <span className="ae-marker mt-2.5" />
          <span>{text}</span>
        </li>
      );
    })}
  </ul>
);

export default FreeLessonRender;
